import type { ReactNode } from 'react';

import {
  SiEslint,
  SiNextdotjs,
  SiVercel,
} from '@icons-pack/react-simple-icons';
import * as NavigationMenu from '@mcdougald/ui/src';
import { Button, Separator, Typography } from '@mcdougald/ui/src';
import {
  ChartColumn,
  ChevronDownIcon,
  FileDown,
  Globe,
  Link as LinkIcon,
  PersonStandingIcon,
  SearchCode,
  Signature,
  Speech,
  UserRound,
} from 'lucide-react';
import Link from 'next/link';

import { NAVIGATION_MENU_BG_CLASS } from '@/components/layout/navbar/constants';
import { NavigationMenuInternalLink } from '@/components/next/nav-menu-internal-link';

import { cn } from '../../../libraries/tailwind/cn';

import { ContentGridLink } from './content-grid-link';

type NavigationItemProps = {
  isActive?: boolean;
  pathname?: string;
};
function ViewAllProjectsLink() {
  return (
    <Link
      id="View-All-Projects-Link"
      data-umami-event="View-All-Projects-Link"
      href="/projects"
      className="group flex p-2 h-full min-h-[200px]"
    >
      <div className="flex p-2 relative hover:outline-blue-50/30 hover:outline-1 rounded-lg">
        <div
          style={{ color: 'gray' }}
          className="absolute after:rounded-lg rounded-lg inset-0 bg-cover h-full after:absolute after:inset-0 after:bg-linear-to-tr/oklch after:from-white after:from-25% after:to-white/60 border border-gray-300"
        />
        <div className="p-2 flex flex-col h-full justify-center z-10 max-w-[240px]">
          <Typography
            variant={'h3'}
            className="group-hover:text-gray-900 text-gray-700 flex"
          >
            View all my projects
            <LinkIcon size={20} className={'ml-2 self-center'} />
          </Typography>
          <p className="text-gray-500 text-balance mt-2">
            Created by me with 🧠 and ❤️
          </p>
        </div>
      </div>
    </Link>
  );
}

function ViewAllPostsLink() {
  return (
    <Link
      id="View-All-Posts-Link"
      data-umami-event="View-All-Posts-Link"
      href="/posts"
      className="group flex p-2 h-full min-h-[200px]"
    >
      <div className="flex p-2 relative hover:outline-blue-50/30 hover:outline-1 rounded-lg">
        <div className="absolute after:rounded-lg rounded-lg inset-0 bg-cover h-full after:absolute after:inset-0 after:bg-linear-to-tr/oklch after:from-white after:from-25% after:to-white/60 border border-gray-300" />
        <div className="p-2 flex flex-col h-full justify-center z-10 max-w-[240px]">
          <Typography
            variant={'h3'}
            className="group-hover:text-gray-900 text-gray-700 flex"
          >
            View all my posts & .mdx docs{' '}
            <LinkIcon size={20} className={'ml-2 self-center'} />
          </Typography>
          <p className="text-gray-500 text-balance mt-2">
            Created by me with 🧠 and ❤️
          </p>

          {/*<p className="text-muted-foreground text-balance">Created by Me</p>*/}
        </div>
      </div>
    </Link>
  );
}

export const ContactNavigationMenuItem = ({
  isActive,
}: NavigationItemProps) => {
  return (
    <NavigationMenu.Item
      className={cn('rounded-sm py-2 text-sm font-medium transition-colors', {
        'text-muted-foreground hover:text-gray-700': !isActive,
        '!text-gray-900 underline': isActive,
      })}
    >
      <NavigationMenuInternalLink href="/contact">
        Contact
      </NavigationMenuInternalLink>
    </NavigationMenu.Item>
  );
};
export const ExperienceNavigationMenuItem = ({
  isActive,
}: NavigationItemProps) => {
  return (
    <NavigationMenu.Item
      className={cn('rounded-sm py-2 text-sm font-medium transition-colors', {
        'text-muted-foreground hover:text-gray-700': !isActive,
        '!text-gray-900 hover:text-gray-700 underline': isActive,
      })}
    >
      <NavigationMenuInternalLink href="/experience">
        Experience
      </NavigationMenuInternalLink>
    </NavigationMenu.Item>
  );
};
/**
 * Goes to /projects
 */
export const ProjectsNavigationMenuItem = ({
  isActive,
}: NavigationItemProps) => {
  return (
    <NavigationMenu.Item
      className={cn(
        'content-center rounded-sm text-sm font-medium transition-colors',
        {
          'text-muted-foreground hover:text-gray-700': !isActive,
          '!text-gray-900 underline': isActive,
        },
      )}
    >
      <NavigationMenu.Trigger className={'cursor-pointer'}>
        <Link href={'/projects'}>Projects</Link>
      </NavigationMenu.Trigger>
      <NavigationMenu.Content
        className={`mx-1 mt-5 absolute rounded-2xl ${NAVIGATION_MENU_BG_CLASS}`}
      >
        <ul className="grid md:grid-cols-[max-content_1fr] gap-1 p-4 w-auto text-sm">
          <li className="row-span-3">
            <NavigationMenu.Link asChild>
              <ViewAllProjectsLink />
            </NavigationMenu.Link>
          </li>
          <ContentGridLink
            href="/projects/this-website"
            title="Portfolo / Blog"
            subtitle="My personal portfolio and blog"
            icon={Globe}
            className={'pt-2'}
          />
          <ContentGridLink
            href="/projects/a-amazon-dev"
            title="A.Amazon.Dev - Internal Amazon AI Analysis"
            subtitle="Project I worked on at Amazon"
            icon={SearchCode}
          />
          <ContentGridLink
            href="/projects/vercel-hosted-apps"
            title="Vercel Hosted Apps"
            subtitle="Projects I host on Vercel"
            icon={SiVercel}
            iconClassName={'!size-6'}
          />
        </ul>
      </NavigationMenu.Content>
    </NavigationMenu.Item>
  );
};
/**
 * Goes to /posts
 */
export const PostsNavigationMenuItem = ({ isActive }: NavigationItemProps) => {
  return (
    <NavigationMenu.Item
      className={cn(
        'content-center rounded-sm text-sm font-medium transition-colors',
        {
          'text-muted-foreground hover:text-gray-700': !isActive,
          '!text-gray-900 underline': isActive,
        },
      )}
    >
      <NavigationMenu.Trigger className={'cursor-pointer'}>
        <Link href={'/posts'}>Posts</Link>
      </NavigationMenu.Trigger>
      <NavigationMenu.Content
        className={`mx-1 mt-5 absolute rounded-2xl ${NAVIGATION_MENU_BG_CLASS}`}
      >
        <ul className="grid md:grid-cols-[max-content_1fr] gap-1 p-4 w-auto text-sm">
          <li className="row-span-3 mr-3">
            <NavigationMenu.Link asChild>
              <ViewAllPostsLink />
            </NavigationMenu.Link>
          </li>
          <ContentGridLink
            href="/posts/nextjs-notes"
            title="NextJS Configs & Projects"
            subtitle="References I've used and noted to build this website"
            icon={SiNextdotjs}
            className={'pt-2'}
          />
          <ContentGridLink
            href="/posts/linting"
            title="Monorepo Static Linting"
            subtitle="ECMAScript-Monorepo Code Management"
            icon={SiEslint}
          />
          <ContentGridLink
            href="/posts/2025"
            title="2025 Updates"
            subtitle="Updates on my career progress & life for current year"
            icon={Signature}
          />
        </ul>
      </NavigationMenu.Content>
    </NavigationMenu.Item>
  );
};
/**
 * Goes to /projects
 */
export const MoreNavigationMenuItem = ({ isActive }: NavigationItemProps) => {
  return (
    <>
      <Separator
        orientation={'vertical'}
        className={'w-[2px] min-h-[32px] flex !bg-gray-300 m-auto ml-3 mr-3'}
      />
      <NavigationMenu.Item
        className={cn(
          'content-center rounded-sm text-sm font-medium transition-colors',
          {
            'hover:text-gray-700': !isActive,
            'text-gray-900 underline': isActive,
          },
        )}
      >
        <NavigationMenu.Trigger
          asChild={false}
          className={'cursor-pointer'}
          icon={false}
        >
          <Button
            asChild={false}
            variant={'ghost'}
            className={cn(
              'bg-primary/5 hover:bg-primary/10 hover:border-primary/5 font-normal !text-gray-600',
              {
                'font-bold': isActive,
                '!text-muted-foreground  hover:!text-gray-700': !isActive,
                '!text-gray-900': isActive,
              },
            )}
          >
            <>
              More{' '}
              <ChevronDownIcon
                className="relative top-[1px] ml-1 h-4 w-4 transition-transform duration-250 group-data-[state=open]:rotate-[-180deg]"
                aria-hidden
              />
            </>
          </Button>
        </NavigationMenu.Trigger>
        <NavigationMenu.Content
          className={`mx-1 mt-5 absolute rounded-2xl ${NAVIGATION_MENU_BG_CLASS}`}
        >
          <ul className="grid md:grid-cols-1 gap-2 p-4 w-auto text-sm w-max">
            {/*<li className="row-span-3">*/}
            {/*  <NavigationMenu.Link asChild>*/}
            {/*    <ViewAllProjectsLink />*/}
            {/*  </NavigationMenu.Link>*/}
            {/*</li>*/}
            <ContentGridLink
              href="/dashboard"
              title="Dashboard"
              subtitle="See my website & career stats"
              icon={ChartColumn}
            />
            <ContentGridLink
              href="/guestbook"
              title="Guestbook"
              subtitle="Sign the guestbook or leave any comments"
              icon={PersonStandingIcon}
            />
            <ContentGridLink
              href="/resumes"
              title="My Resumes"
              subtitle="View / download my current resume"
              icon={FileDown}
            />
            <ContentGridLink
              href="/about"
              title="Learn More About Me"
              subtitle="Learn more about what I'm up to"
              icon={UserRound}
            />
            <ContentGridLink
              href="/experience#references"
              title="References"
              subtitle="See referrals and testimonials about my work"
              icon={Speech}
            />
          </ul>
        </NavigationMenu.Content>
      </NavigationMenu.Item>
    </>
  );
};
export type HeaderNavLink = {
  icon: ReactNode;
  href: string;
  key: string;
  isActive: (pathname: string) => boolean;
  RenderNavItem: (props: NavigationItemProps) => ReactNode;
};
